import { createHash } from "node:crypto";
import { constants } from "node:fs";
import * as fs from "node:fs/promises";
import * as path from "node:path";

export type CompanionArtifactPlatform = "darwin" | "win32";
export type CompanionArtifactArchitecture = "arm64" | "x64";

export type ArtifactEntry =
  | {
      readonly type: "file";
      readonly path: string;
      readonly mode: number;
      readonly size: number;
      readonly sha256: string;
    }
  | {
      readonly type: "symlink";
      readonly path: string;
      readonly target: string;
    };

export interface CompanionArtifactManifest {
  readonly schemaVersion: 1;
  readonly platform: CompanionArtifactPlatform;
  readonly architecture: CompanionArtifactArchitecture;
  readonly version: string;
  readonly entryRelativePath: string;
  readonly treeSha256: string;
  readonly entries: readonly ArtifactEntry[];
}

interface BuildManifestInput {
  readonly platform: CompanionArtifactPlatform;
  readonly architecture: CompanionArtifactArchitecture;
  readonly version: string;
  readonly entryRelativePath: string;
}

const SHA256 = /^[a-f0-9]{64}$/u;
const VERSION = /^[A-Za-z0-9][A-Za-z0-9._+-]{0,127}$/u;

function exactKeys(value: Record<string, unknown>, expected: readonly string[]): boolean {
  const actual = Object.keys(value).sort();
  return (
    actual.length === expected.length &&
    actual.every((key, index) => key === [...expected].sort()[index])
  );
}

function normalizedRelative(value: string): string {
  if (
    value.length === 0 ||
    value.includes("\\") ||
    value.includes("\0") ||
    path.posix.isAbsolute(value)
  ) {
    throw new Error("Artifact path must be a normalized relative POSIX path");
  }
  const normalized = path.posix.normalize(value);
  if (
    normalized !== value ||
    normalized === "." ||
    normalized === ".." ||
    normalized.startsWith("../")
  ) {
    throw new Error("Artifact path escapes the payload root");
  }
  return value;
}

function validateLink(entryPath: string, target: string): string {
  if (
    target.length === 0 ||
    target.includes("\\") ||
    target.includes("\0") ||
    path.posix.isAbsolute(target)
  ) {
    throw new Error("Artifact symlink target must be relative");
  }
  const resolved = path.posix.normalize(
    path.posix.join(path.posix.dirname(entryPath), target),
  );
  if (
    resolved === ".." ||
    resolved.startsWith("../") ||
    path.posix.isAbsolute(resolved)
  ) {
    throw new Error("Artifact symlink target escapes the payload root");
  }
  return target;
}

function canonicalEntry(entry: ArtifactEntry): string {
  return entry.type === "file"
    ? `file\0${entry.path}\0${String(entry.mode)}\0${String(entry.size)}\0${entry.sha256}\n`
    : `symlink\0${entry.path}\0${entry.target}\n`;
}

/**
 * A digest of the entries themselves, not of the order something happened to
 * produce them in. Sorted here because the two sides that compare it do not
 * enumerate alike: a manifest lists entries sorted over whole paths, while
 * walking a tree descends into a directory where it finds it, so
 * `resources/app.asar` arrives before its sibling `resources.pak`. Hashing
 * whatever order the caller had made the digest disagree with itself, and no
 * Windows build could be verified. Paths are unique, so this is a total order.
 */
export function artifactTreeSha256(entries: readonly ArtifactEntry[]): string {
  const hash = createHash("sha256");
  const ordered = [...entries].sort((left, right) =>
    left.path.localeCompare(right.path),
  );
  for (const entry of ordered) hash.update(canonicalEntry(entry), "utf8");
  return hash.digest("hex");
}

function parseEntry(value: unknown): ArtifactEntry {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Artifact manifest entry schema is invalid");
  }
  const record = value as Record<string, unknown>;
  if (record.type === "file") {
    if (
      !exactKeys(record, ["type", "path", "mode", "size", "sha256"]) ||
      typeof record.path !== "string" ||
      typeof record.mode !== "number" ||
      !Number.isSafeInteger(record.mode) ||
      record.mode < 0 ||
      record.mode > 0o777 ||
      typeof record.size !== "number" ||
      !Number.isSafeInteger(record.size) ||
      record.size < 0 ||
      typeof record.sha256 !== "string" ||
      !SHA256.test(record.sha256)
    ) {
      throw new Error("Artifact file entry schema is invalid");
    }
    return {
      type: "file",
      path: normalizedRelative(record.path),
      mode: record.mode,
      size: record.size,
      sha256: record.sha256,
    };
  }
  if (
    record.type === "symlink" &&
    exactKeys(record, ["type", "path", "target"]) &&
    typeof record.path === "string" &&
    typeof record.target === "string"
  ) {
    const entryPath = normalizedRelative(record.path);
    return {
      type: "symlink",
      path: entryPath,
      target: validateLink(entryPath, record.target),
    };
  }
  throw new Error("Artifact symlink entry schema is invalid");
}

export function parseArtifactManifest(value: unknown): CompanionArtifactManifest {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Artifact manifest schema is invalid");
  }
  const record = value as Record<string, unknown>;
  if (
    !exactKeys(record, [
      "schemaVersion",
      "platform",
      "architecture",
      "version",
      "entryRelativePath",
      "treeSha256",
      "entries",
    ]) ||
    record.schemaVersion !== 1 ||
    (record.platform !== "darwin" && record.platform !== "win32") ||
    (record.architecture !== "arm64" && record.architecture !== "x64") ||
    typeof record.version !== "string" ||
    !VERSION.test(record.version) ||
    typeof record.entryRelativePath !== "string" ||
    typeof record.treeSha256 !== "string" ||
    !SHA256.test(record.treeSha256) ||
    !Array.isArray(record.entries)
  ) {
    throw new Error("Artifact manifest schema is invalid");
  }
  const entries = record.entries.map(parseEntry);
  const paths = entries.map((entry) => entry.path);
  const sorted = [...paths].sort((left, right) => left.localeCompare(right));
  if (
    paths.some((entryPath, index) => entryPath !== sorted[index]) ||
    new Set(paths).size !== paths.length
  ) {
    throw new Error("Artifact entries must be unique and sorted");
  }
  if (artifactTreeSha256(entries) !== record.treeSha256) {
    throw new Error("Artifact tree digest does not match entries");
  }
  return {
    schemaVersion: 1,
    platform: record.platform,
    architecture: record.architecture,
    version: record.version,
    entryRelativePath: normalizedRelative(record.entryRelativePath),
    treeSha256: record.treeSha256,
    entries,
  };
}

async function sha256File(filePath: string): Promise<string> {
  return createHash("sha256").update(await fs.readFile(filePath)).digest("hex");
}

async function walk(root: string, relative = ""): Promise<ArtifactEntry[]> {
  const directory = path.join(root, ...relative.split("/").filter(Boolean));
  const children = await fs.readdir(directory, { withFileTypes: true });
  const entries: ArtifactEntry[] = [];
  for (const child of children.sort((left, right) => left.name.localeCompare(right.name))) {
    const childRelative = relative ? `${relative}/${child.name}` : child.name;
    normalizedRelative(childRelative);
    const childPath = path.join(directory, child.name);
    const stat = await fs.lstat(childPath);
    if (stat.isDirectory()) {
      entries.push(...(await walk(root, childRelative)));
    } else if (stat.isFile()) {
      entries.push({
        type: "file",
        path: childRelative,
        mode: stat.mode & 0o777,
        size: stat.size,
        sha256: await sha256File(childPath),
      });
    } else if (stat.isSymbolicLink()) {
      const target = await fs.readlink(childPath);
      entries.push({
        type: "symlink",
        path: childRelative,
        target: validateLink(childRelative, target),
      });
    } else {
      throw new Error(`Unsupported artifact entry: ${childRelative}`);
    }
  }
  return entries;
}

async function ensureRealDirectory(root: string): Promise<void> {
  const stat = await fs.lstat(root);
  if (stat.isSymbolicLink()) throw new Error("Artifact root is a symbolic link");
  if (!stat.isDirectory()) throw new Error("Artifact root is not a directory");
}

async function validateResolvedLinks(
  root: string,
  entries: readonly ArtifactEntry[],
): Promise<void> {
  const realRoot = await fs.realpath(root);
  for (const entry of entries) {
    if (entry.type !== "symlink") continue;
    try {
      const linkPath = path.join(root, ...entry.path.split("/"));
      const resolved = await fs.realpath(linkPath);
      const relative = path.relative(realRoot, resolved);
      if (
        relative === ".." ||
        relative.startsWith(`..${path.sep}`) ||
        path.isAbsolute(relative)
      ) {
        throw new Error("Artifact symlink chain escapes the payload root");
      }
    } catch (error) {
      if (
        error instanceof Error &&
        error.message.includes("Artifact symlink chain")
      ) {
        throw error;
      }
      throw new Error(`Artifact symlink does not resolve inside payload: ${entry.path}`);
    }
  }
}

export async function buildArtifactManifest(
  payloadRoot: string,
  input: BuildManifestInput,
): Promise<CompanionArtifactManifest> {
  await ensureRealDirectory(payloadRoot);
  const entries = await walk(payloadRoot);
  await validateResolvedLinks(payloadRoot, entries);
  const manifest: CompanionArtifactManifest = {
    schemaVersion: 1,
    platform: input.platform,
    architecture: input.architecture,
    version: input.version,
    entryRelativePath: normalizedRelative(input.entryRelativePath),
    treeSha256: artifactTreeSha256(entries),
    entries,
  };
  if (!entries.some((entry) => entry.path === manifest.entryRelativePath && entry.type === "file")) {
    throw new Error("Artifact executable is missing from the payload");
  }
  return manifest;
}

export async function readArtifactManifest(
  manifestPath: string,
): Promise<CompanionArtifactManifest> {
  const before = await fs.lstat(manifestPath);
  if (before.isSymbolicLink() || !before.isFile()) {
    throw new Error("Artifact manifest is a symbolic link or non-file");
  }
  let handle: fs.FileHandle | undefined;
  try {
    handle = await fs.open(
      manifestPath,
      constants.O_RDONLY | constants.O_NOFOLLOW,
    );
    const opened = await handle.stat();
    const sameFile =
      (before.ino === 0 && opened.ino === 0) ||
      (before.dev === opened.dev && before.ino === opened.ino);
    if (!opened.isFile() || !sameFile || opened.size > 4 * 1024 * 1024) {
      throw new Error("Artifact manifest is not a bounded regular file");
    }
    return parseArtifactManifest(
      JSON.parse(await handle.readFile("utf8")) as unknown,
    );
  } finally {
    await handle?.close().catch(() => undefined);
  }
}

function ensureContainedEntry(root: string, entryPath: string): string {
  const absolute = path.join(root, ...entryPath.split("/"));
  const relative = path.relative(root, absolute);
  if (relative.startsWith("..") || path.isAbsolute(relative)) {
    throw new Error("Artifact entry escapes payload root");
  }
  return absolute;
}

/**
 * Verify a payload that arrived inside a packed package rather than one this
 * CLI wrote itself.
 *
 * npm builds its tarballs without symbolic links. The macOS bundle carries
 * fourteen of them — `Contents/Frameworks/Electron Framework.framework/
 * Electron Framework` and its siblings — and every one is silently absent from
 * an installed package while the files around them arrive byte-for-byte. A
 * payload is therefore held to its files, and to nothing being present that the
 * manifest does not declare.
 *
 * The links cost nothing to lose here because they were never read from the
 * payload: copyArtifactPayload creates each one from the manifest, whose
 * targets are already held to being relative and to resolving inside the
 * payload, and then verifies the tree it produced in full.
 */
export async function verifyArtifactSource(
  payloadRoot: string,
  manifest: CompanionArtifactManifest,
): Promise<void> {
  await ensureRealDirectory(payloadRoot);
  const actual = await walk(payloadRoot);
  await validateResolvedLinks(payloadRoot, actual);
  const declared = new Map(manifest.entries.map((entry) => [entry.path, entry]));
  for (const entry of actual) {
    if (JSON.stringify(declared.get(entry.path)) !== JSON.stringify(entry)) {
      throw new Error(`Artifact entry does not match manifest: ${entry.path}`);
    }
  }
  const present = new Set(actual.map((entry) => entry.path));
  for (const entry of manifest.entries) {
    if (entry.type === "file" && !present.has(entry.path)) {
      throw new Error(`Artifact file is missing from the payload: ${entry.path}`);
    }
  }
}

export async function verifyArtifactPayload(
  payloadRoot: string,
  manifest: CompanionArtifactManifest,
): Promise<void> {
  await ensureRealDirectory(payloadRoot);
  const actual = await walk(payloadRoot);
  await validateResolvedLinks(payloadRoot, actual);
  if (
    actual.length !== manifest.entries.length ||
    artifactTreeSha256(actual) !== manifest.treeSha256
  ) {
    throw new Error("Artifact payload digest or size does not match manifest");
  }
  for (const entry of manifest.entries) {
    const actualEntry = actual.find((candidate) => candidate.path === entry.path);
    if (JSON.stringify(actualEntry) !== JSON.stringify(entry)) {
      throw new Error(`Artifact entry does not match manifest: ${entry.path}`);
    }
  }
}

export async function copyArtifactPayload(
  sourceRoot: string,
  targetRoot: string,
  manifest: CompanionArtifactManifest,
): Promise<void> {
  await verifyArtifactSource(sourceRoot, manifest);
  try {
    const targetStat = await fs.lstat(targetRoot);
    if (targetStat.isSymbolicLink()) {
      throw new Error("Install target is a symbolic link");
    }
    if (!targetStat.isDirectory()) throw new Error("Install target is not a directory");
    if ((await fs.readdir(targetRoot)).length > 0) {
      throw new Error("Install staging target is not empty");
    }
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code !== "ENOENT") throw error;
    await fs.mkdir(targetRoot, { recursive: false, mode: 0o700 });
  }
  for (const entry of manifest.entries) {
    const source = ensureContainedEntry(sourceRoot, entry.path);
    const target = ensureContainedEntry(targetRoot, entry.path);
    await fs.mkdir(path.dirname(target), { recursive: true, mode: 0o700 });
    if (entry.type === "file") {
      await fs.copyFile(source, target);
      await fs.chmod(target, entry.mode);
    } else {
      await fs.symlink(entry.target, target);
    }
  }
  await verifyArtifactPayload(targetRoot, manifest);
}
